import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { SwitchFormField } from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, CardHeader, CardTitle, Separator, Spinner } from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  ProfileEditPushNotificationSettingsQuery,
  ProfileEditPushNotificationSettingsQueryVariables,
  UpdateSettingsNotificationsInput,
  UpdateSettingsNotificationsMutation,
  UpdateSettingsNotificationsMutationVariables,
} from 'types/graphql'

const PROFILE_EDIT_PUSH_NOTIFICATION_SETTINGS_QUERY: TypedDocumentNode<
  ProfileEditPushNotificationSettingsQuery,
  ProfileEditPushNotificationSettingsQueryVariables
> = gql`
  query ProfileEditPushNotificationSettingsQuery($profileId: String!) {
    settings: settingsNotifications(profileId: $profileId) {
      id
      albumShare
      commentEvent
      commentMedia
      commentPost
      commentProfile
      commentReply
      eventJoinRequest
      eventJoinRequestApproved
      eventJoinRequestRejected
      eventAttendanceStatusChange
      eventAttendeeLeft
      eventAttendanceStatusChangeHost
      eventAttendeeLeftHost
      eventInvitation
      eventCancelled
      eventUpdated
      eventPostNew
      eventReminder
      friendApproved
      friendRequest
      groupInvitationReceived
      groupJoinRequestApproved
      groupJoinRequestReceived
      groupJoinRequestRejected
      groupMemberAdded
      groupMemberRemoved
      groupMemberBanned
      groupMemberUnbanned
      groupMemberRoleChanged
      groupPostApproved
      groupPostPending
      groupPostRejected
      likeComment
      likeMedia
      likePost
      likeProfile
      mentionPost
      mentionComment
      messageNew
      membershipExpiryReminder
      reactionComment
      reactionMedia
      reactionPost
      reactionProfile
      reviewPlace
      reviewPublication
      reviewReply
      verificationApproved
      verificationSubmitted
      ownerId
    }
  }
`

const UPDATE_SETTINGS_NOTIFICATIONS_MUTATION: TypedDocumentNode<
  UpdateSettingsNotificationsMutation,
  UpdateSettingsNotificationsMutationVariables
> = gql`
  mutation UpdateSettingsNotifications($profileId: String!, $input: UpdateSettingsNotificationsInput!) {
    updateSettingsNotifications(profileId: $profileId, input: $input) {
      id
    }
  }
`

type NotificationSettings = NonNullable<ProfileEditPushNotificationSettingsQuery['settings']>

export const Route = createFileRoute('/(auth)/(account-layout)/account/$profileId/notifications')({
  component: EditPushNotificationSettingsPage,
  head: () =>
    seo({
      title: `Push Notification Settings | ${env.VITE_APP_NAME}`,
      description: 'Push Notification Settings',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, PROFILE_EDIT_PUSH_NOTIFICATION_SETTINGS_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function EditPushNotificationSettingsPage() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<ProfileEditPushNotificationSettingsQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const handleRefetch = () => {
    return apolloClient.refetchQueries({ include: [PROFILE_EDIT_PUSH_NOTIFICATION_SETTINGS_QUERY] })
  }

  const [showSpinner, setShowSpinnerValue] = useState(false)

  const settings = data?.settings as NotificationSettings | undefined

  const formMethods = useForm({
    defaultValues: {
      albumShare: settings?.albumShare ?? false,
      commentEvent: settings?.commentEvent ?? false,
      commentMedia: settings?.commentMedia ?? false,
      commentPost: settings?.commentPost ?? false,
      commentProfile: settings?.commentProfile ?? false,
      commentReply: settings?.commentReply ?? false,
      eventJoinRequestApproved: settings?.eventJoinRequestApproved ?? false,
      eventJoinRequestRejected: settings?.eventJoinRequestRejected ?? false,
      eventJoinRequest: settings?.eventJoinRequest ?? false,
      eventAttendanceStatusChange: settings?.eventAttendanceStatusChange ?? false,
      eventAttendeeLeft: settings?.eventAttendeeLeft ?? false,
      eventAttendanceStatusChangeHost: settings?.eventAttendanceStatusChangeHost ?? false,
      eventAttendeeLeftHost: settings?.eventAttendeeLeftHost ?? false,
      eventInvitation: settings?.eventInvitation ?? false,
      eventCancelled: settings?.eventCancelled ?? false,
      eventUpdated: settings?.eventUpdated ?? false,
      eventPostNew: settings?.eventPostNew ?? false,
      eventReminder: settings?.eventReminder ?? false,
      friendApproved: settings?.friendApproved ?? false,
      friendRequest: settings?.friendRequest ?? false,
      groupInvitationReceived: settings?.groupInvitationReceived ?? false,
      groupJoinRequestApproved: settings?.groupJoinRequestApproved ?? false,
      groupJoinRequestReceived: settings?.groupJoinRequestReceived ?? false,
      groupJoinRequestRejected: settings?.groupJoinRequestRejected ?? false,
      groupMemberAdded: settings?.groupMemberAdded ?? false,
      groupMemberRemoved: settings?.groupMemberRemoved ?? false,
      groupMemberBanned: settings?.groupMemberBanned ?? false,
      groupMemberUnbanned: settings?.groupMemberUnbanned ?? false,
      groupMemberRoleChanged: settings?.groupMemberRoleChanged ?? false,
      groupPostApproved: settings?.groupPostApproved ?? false,
      groupPostPending: settings?.groupPostPending ?? false,
      groupPostRejected: settings?.groupPostRejected ?? false,
      likeComment: settings?.likeComment ?? false,
      likeMedia: settings?.likeMedia ?? false,
      likePost: settings?.likePost ?? false,
      likeProfile: settings?.likeProfile ?? false,
      mentionPost: settings?.mentionPost ?? false,
      mentionComment: settings?.mentionComment ?? false,
      messageNew: settings?.messageNew ?? false,
      membershipExpiryReminder: settings?.membershipExpiryReminder ?? false,
      reactionComment: settings?.reactionComment ?? false,
      reactionMedia: settings?.reactionMedia ?? false,
      reactionPost: settings?.reactionPost ?? false,
      reactionProfile: settings?.reactionProfile ?? false,
      reviewPlace: settings?.reviewPlace ?? false,
      reviewPublication: settings?.reviewPublication ?? false,
      reviewReply: settings?.reviewReply ?? false,
      verificationApproved: settings?.verificationApproved ?? false,
      verificationSubmitted: settings?.verificationSubmitted ?? false,
    },
  })

  const { isDirty } = useFormState({ control: formMethods.control })

  useEffect(() => {
    if (settings) {
      formMethods.reset({
        albumShare: settings.albumShare,
        commentEvent: settings.commentEvent,
        commentMedia: settings.commentMedia,
        commentPost: settings.commentPost,
        commentProfile: settings.commentProfile,
        commentReply: settings.commentReply,
        eventJoinRequestApproved: settings.eventJoinRequestApproved,
        eventJoinRequestRejected: settings.eventJoinRequestRejected,
        eventJoinRequest: settings.eventJoinRequest,
        eventAttendanceStatusChange: settings.eventAttendanceStatusChange,
        eventAttendeeLeft: settings.eventAttendeeLeft,
        eventAttendanceStatusChangeHost: settings.eventAttendanceStatusChangeHost,
        eventAttendeeLeftHost: settings.eventAttendeeLeftHost,
        eventInvitation: settings.eventInvitation,
        eventCancelled: settings.eventCancelled,
        eventUpdated: settings.eventUpdated,
        eventPostNew: settings.eventPostNew,
        eventReminder: settings.eventReminder,
        friendApproved: settings.friendApproved,
        friendRequest: settings.friendRequest,
        groupInvitationReceived: settings.groupInvitationReceived,
        groupJoinRequestApproved: settings.groupJoinRequestApproved,
        groupJoinRequestReceived: settings.groupJoinRequestReceived,
        groupJoinRequestRejected: settings.groupJoinRequestRejected,
        groupMemberAdded: settings.groupMemberAdded,
        groupMemberRemoved: settings.groupMemberRemoved,
        groupMemberBanned: settings.groupMemberBanned,
        groupMemberUnbanned: settings.groupMemberUnbanned,
        groupMemberRoleChanged: settings.groupMemberRoleChanged,
        groupPostApproved: settings.groupPostApproved,
        groupPostPending: settings.groupPostPending,
        groupPostRejected: settings.groupPostRejected,
        likeComment: settings.likeComment,
        likeMedia: settings.likeMedia,
        likePost: settings.likePost,
        likeProfile: settings.likeProfile,
        mentionPost: settings.mentionPost,
        mentionComment: settings.mentionComment,
        messageNew: settings.messageNew,
        membershipExpiryReminder: settings.membershipExpiryReminder,
        reactionComment: settings.reactionComment,
        reactionMedia: settings.reactionMedia,
        reactionPost: settings.reactionPost,
        reactionProfile: settings.reactionProfile,
        reviewPlace: settings.reviewPlace,
        reviewPublication: settings.reviewPublication,
        reviewReply: settings.reviewReply,
        verificationApproved: settings.verificationApproved,
        verificationSubmitted: settings.verificationSubmitted,
      })
    }
  }, [
    settings?.albumShare,
    settings?.commentEvent,
    settings?.commentMedia,
    settings?.commentPost,
    settings?.commentProfile,
    settings?.commentReply,
    settings?.eventJoinRequestApproved,
    settings?.eventJoinRequestRejected,
    settings?.eventJoinRequest,
    settings?.eventAttendanceStatusChange,
    settings?.eventAttendeeLeft,
    settings?.eventAttendanceStatusChangeHost,
    settings?.eventAttendeeLeftHost,
    settings?.eventInvitation,
    settings?.eventCancelled,
    settings?.eventUpdated,
    settings?.eventPostNew,
    settings?.eventReminder,
    settings?.friendApproved,
    settings?.friendRequest,
    settings?.groupInvitationReceived,
    settings?.groupJoinRequestApproved,
    settings?.groupJoinRequestReceived,
    settings?.groupJoinRequestRejected,
    settings?.groupMemberAdded,
    settings?.groupMemberRemoved,
    settings?.groupMemberBanned,
    settings?.groupMemberUnbanned,
    settings?.groupMemberRoleChanged,
    settings?.groupPostApproved,
    settings?.groupPostPending,
    settings?.groupPostRejected,
    settings?.likeComment,
    settings?.likeMedia,
    settings?.likePost,
    settings?.likeProfile,
    settings?.mentionPost,
    settings?.mentionComment,
    settings?.messageNew,
    settings?.membershipExpiryReminder,
    settings?.reactionComment,
    settings?.reactionMedia,
    settings?.reactionPost,
    settings?.reactionProfile,
    settings?.reviewPlace,
    settings?.reviewPublication,
    settings?.reviewReply,
    settings?.verificationApproved,
    settings?.verificationSubmitted,
    formMethods,
    settings,
  ])

  const [updateSettings] = useMutation(UPDATE_SETTINGS_NOTIFICATIONS_MUTATION, {
    onCompleted: () => {
      toast.success(t('Push Notification Settings Updated'))
      handleRefetch()
      setShowSpinnerValue(false)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinnerValue(false)
    },
  })

  const onSubmit = (fieldData: UpdateSettingsNotificationsInput) => {
    if (!settings) return
    setShowSpinnerValue(true)

    updateSettings({
      variables: {
        profileId: settings.ownerId,
        input: fieldData,
      },
    })
  }

  const settingsGroups = useMemo(
    (): Array<{
      title: string
      description?: string
      settings: { key: keyof UpdateSettingsNotificationsInput; title: string }[]
    }> => [
      {
        title: t('Membership: Notify On...'),
        settings: [
          {
            key: 'membershipExpiryReminder' as const,
            title: t('Supporter & preview access ending (in-app / push before your role expires)'),
          },
        ],
      },
      {
        title: t('Albums: Notify On...'),
        settings: [{ key: 'albumShare' as const, title: t('Album Shared With You') }],
      },
      {
        title: t('Mentions: Notify On...'),
        settings: [
          { key: 'mentionPost' as const, title: t('Someone mentions you in a post') },
          { key: 'mentionComment' as const, title: t('Someone mentions you in a comment') },
        ],
      },
      {
        title: t('Comments: Notify On...'),
        settings: [
          { key: 'commentEvent' as const, title: t('New Comments For Events You Maintain') },
          { key: 'commentMedia' as const, title: t('New Comments For Your Media') },
          { key: 'commentPost' as const, title: t('New Comments For Your Posts') },
          { key: 'commentProfile' as const, title: t('New Comments For Your Profile') },
          { key: 'commentReply' as const, title: t('Replies To Your Comments') },
        ],
      },
      {
        title: t('Events: Notify On...'),
        settings: [
          { key: 'eventInvitation' as const, title: t('Event Invitation') },
          { key: 'eventJoinRequestApproved' as const, title: t('Event Access Approved') },
          { key: 'eventJoinRequestRejected' as const, title: t('Event Access Rejected') },
          { key: 'eventCancelled' as const, title: t('Event Cancellation') },
          { key: 'eventUpdated' as const, title: t('Event Key Detail Changes') },
          { key: 'eventPostNew' as const, title: t('Event New Post') },
          { key: 'eventReminder' as const, title: t('Event Reminders') },
          { key: 'eventAttendanceStatusChange' as const, title: t('Event Attendance Status Changes') },
          { key: 'eventAttendeeLeft' as const, title: t('Removal From An Event') },
        ],
      },
      {
        title: t('Events Maintainer: Notify On...'),
        settings: [
          { key: 'eventJoinRequest' as const, title: t('Access Request To Your Event') },
          { key: 'eventAttendanceStatusChangeHost' as const, title: t('Attendee Status Changed') },
          { key: 'eventAttendeeLeftHost' as const, title: t('Attendee Left Your Event') },
        ],
      },
      {
        title: t('Friends: Notify On...'),
        settings: [
          { key: 'friendApproved' as const, title: t('Friend Approvals') },
          { key: 'friendRequest' as const, title: t('Friend Requests') },
        ],
      },
      {
        title: t('Groups: Notify On...'),
        settings: [
          { key: 'groupInvitationReceived' as const, title: t('Invitations') },
          { key: 'groupJoinRequestApproved' as const, title: t('Join Request Approved') },
          { key: 'groupJoinRequestRejected' as const, title: t('Join Request Rejected') },
          { key: 'groupMemberBanned' as const, title: t('Getting Banned') },
          { key: 'groupMemberRoleChanged' as const, title: t('Role Changed') },
          { key: 'groupMemberUnbanned' as const, title: t('Getting Unbanned') },
          { key: 'groupPostApproved' as const, title: t('Post Approved') },
          { key: 'groupPostRejected' as const, title: t('Post Rejected') },
        ],
      },
      {
        title: t('Group Admins: Notify On...'),
        settings: [
          { key: 'groupJoinRequestReceived' as const, title: t('Join Request Received') },
          { key: 'groupMemberAdded' as const, title: t('Member Added') },
          { key: 'groupMemberRemoved' as const, title: t('Member Removed') },
          { key: 'groupPostPending' as const, title: t('Post Pending Review') },
        ],
      },
      {
        title: t('Likes & Reactions: Notify On...'),
        settings: [
          { key: 'likeComment' as const, title: t('Comment Likes') },
          { key: 'reactionComment' as const, title: t('Comment Reactions') },
          { key: 'likeMedia' as const, title: t('Media Likes') },
          { key: 'likePost' as const, title: t('Post Likes') },
          { key: 'reactionPost' as const, title: t('Post Reactions') },
          { key: 'likeProfile' as const, title: t('Profile Likes') },
        ],
      },
      {
        title: t('Chat: Notify On...'),
        settings: [
          {
            key: 'messageNew' as const,
            title: t('New messages, invites, and reactions'),
          },
        ],
      },
      {
        title: t('Reviews: Notify On...'),
        settings: [
          { key: 'reviewPlace' as const, title: t('New Reviews For Your Places') },
          { key: 'reviewPublication' as const, title: t('New Reviews For Your Cliterature') },
          { key: 'reviewReply' as const, title: t('Replies To Your Reviews') },
        ],
      },
    ],
    [t]
  )

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="ml-[200px] pt-3 text-destructive">{t('No Data')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex w-full flex-col">
      <Form className="w-full" formMethods={formMethods} onSubmit={onSubmit}>
        <div className="flex w-full flex-col items-stretch gap-4">
          {settingsGroups.map((group) => (
            <Card className="w-full" key={group.title}>
              <CardHeader>
                <div className="mb-2 flex w-full items-center justify-between gap-4">
                  <CardTitle>{group.title}</CardTitle>
                  <span className="font-medium text-foreground text-lg">{t('Enabled')}</span>
                </div>
                {group.description ? <p className="mb-2 text-muted-foreground text-sm">{group.description}</p> : null}
                <Separator />
              </CardHeader>
              <CardContent>
                <div className="flex w-full flex-col">
                  {group.settings.map((setting, idx) => (
                    <div
                      className={cn(
                        'flex w-full items-center justify-between gap-4 rounded-md p-2',
                        idx % 2 === 1 && 'bg-muted/30'
                      )}
                      key={setting.key}
                    >
                      <span className="text-foreground">{setting.title}</span>
                      <SwitchFormField fieldProps={{ className: 'w-auto shrink-0' }} name={setting.key} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-6 flex w-full justify-end gap-2">
          {isDirty ? (
            <Button onClick={() => formMethods.reset()} type="button" variant="outline">
              {t('Cancel')}
            </Button>
          ) : null}
          <Button disabled={!isDirty || showSpinner} type="submit" variant="default">
            {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
            {t('Save')}
          </Button>
        </div>
      </Form>
    </div>
  )
}
